import React, { memo, useCallback, useMemo } from 'react';

import { Box, Button, Typography } from '@mui/material';
import { ArrowCheckIcon } from '@sharedCR/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { IconDirection, IconSize } from 'constants/coreDesignSystem';

import { getDrawerAuthorKey, getRelativeTime } from '../../PendingChangesDrawer.utils';
import PendingChangesCard from '../PendingChangesCard';
import PendingChangesRecalculatedCard from '../PendingChangesRecalculatedCard';
import { RECALCULATED_CARD_TITLE } from '../PendingChangesRecalculatedCard/PendingChangesRecalculatedCard.constants';

import {
  affectedListStyles,
  affectedToggleStyles,
  groupStyles,
  metaStyles,
} from './PendingChangesOperationGroup.styles';
import { PendingChangesOperationGroupTestIds } from './PendingChangesOperationGroup.testIds';
import { getAffectedToggleText, getOperationMetaText } from './PendingChangesOperationGroup.utils';

const PendingChangesOperationGroup = ({
  group,
  authorColorMap,
  isAffectedExpanded = false,
  isExactTimeShown = false,
  recalculatedCardTitle = RECALCULATED_CARD_TITLE,
  onToggleAffected,
  onNavigate,
}) => {
  const { key, directChanges, affectedChanges, isRecalculatedOnly } = group;

  const hasAffectedChanges = affectedChanges.length > 0;
  const isMetaShown = !isRecalculatedOnly && (hasAffectedChanges || directChanges.length > 1);

  const metaText = useMemo(
    () =>
      getOperationMetaText(
        group,
        isExactTimeShown ? group.modifiedOn : getRelativeTime(group.modifiedOn),
      ),
    [group, isExactTimeShown],
  );

  const onToggleAffectedClick = useCallback(() => {
    onToggleAffected(key);
  }, [onToggleAffected, key]);

  return (
    <Box data-testid={PendingChangesOperationGroupTestIds.GROUP} sx={groupStyles}>
      {isMetaShown && (
        <Typography
          data-testid={PendingChangesOperationGroupTestIds.META}
          sx={metaStyles}
          variant="regular12"
        >
          {metaText}
        </Typography>
      )}

      {isRecalculatedOnly && (
        <PendingChangesRecalculatedCard
          authorColor={authorColorMap[group.authorKey]}
          formattedFullName={group.formattedFullName}
          modifiedOn={group.modifiedOn}
          title={recalculatedCardTitle}
        />
      )}

      {directChanges.map(change => (
        <PendingChangesCard
          authorColor={authorColorMap[getDrawerAuthorKey(change)]}
          change={change}
          key={change.id}
          onNavigate={onNavigate}
        />
      ))}

      {hasAffectedChanges && (
        <Button
          aria-expanded={isAffectedExpanded}
          data-testid={PendingChangesOperationGroupTestIds.AFFECTED_TOGGLE}
          sx={affectedToggleStyles}
          variant="text"
          onClick={onToggleAffectedClick}
        >
          <ArrowCheckIcon
            direction={isAffectedExpanded ? IconDirection.DOWN : IconDirection.RIGHT}
            size={IconSize[14]}
          />
          <Typography variant="medium12">{getAffectedToggleText(group)}</Typography>
        </Button>
      )}

      {hasAffectedChanges && isAffectedExpanded && (
        <Box
          data-testid={PendingChangesOperationGroupTestIds.AFFECTED_LIST}
          sx={affectedListStyles}
        >
          {affectedChanges.map(change => (
            <PendingChangesCard
              isAffected
              change={change}
              key={change.id}
              onNavigate={onNavigate}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

PendingChangesOperationGroup.propTypes = {
  group: PropTypes.shape({
    key: PropTypes.string.isRequired,
    directChanges: PropTypes.array.isRequired,
    affectedChanges: PropTypes.array.isRequired,
    allAffectedChanges: PropTypes.array,
    totalAffectedCount: PropTypes.number,
    isAffectedNarrowed: PropTypes.bool,
    isRecalculatedOnly: PropTypes.bool,
    isWrittenBySave: PropTypes.bool,
    tabName: PropTypes.string,
    authorKey: PropTypes.string,
    formattedFullName: PropTypes.string,
    modifiedOn: PropTypes.string,
  }).isRequired,
  authorColorMap: PropTypes.object.isRequired,
  onToggleAffected: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  isAffectedExpanded: PropTypes.bool,
  isExactTimeShown: PropTypes.bool,
  recalculatedCardTitle: PropTypes.string,
};

export default memo(PendingChangesOperationGroup);
